<template>
  <div>

    <!-- ── Pro→Family upgrade welcome ── -->
    <div v-if="step === 1 && isUpgrade && isSkipSkills" class="ob-card ob-welcome-card">
      <div class="ob-welcome-badge">⭐ Family Plan</div>
      <div class="ob-welcome-icon">🎉</div>
      <h2 class="ob-h2 ob-welcome-h2">Welcome to Family<span v-if="childName">, {{ childName }}'s family</span>!</h2>
      <p class="ob-sub ob-welcome-sub">Your plan is upgraded. Here's everything now unlocked for your family.</p>

      <div class="ob-welcome-perks">
        <div class="ob-welcome-perk">
          <span class="ob-perk-icon">👨‍👩‍👧‍👦</span>
          <div>
            <div class="ob-perk-title">Up to 3 child profiles</div>
            <div class="ob-perk-desc">Add up to 6 children and track each one separately from the parent dashboard</div>
          </div>
        </div>
        <div class="ob-welcome-perk">
          <span class="ob-perk-icon">🎯</span>
          <div>
            <div class="ob-perk-title">Skills Diagnostic <span class="ob-perk-done">✓ Done</span></div>
            <div class="ob-perk-desc">Already completed — each new child you add can run their own Skills Check from their dashboard</div>
          </div>
        </div>
        <div class="ob-welcome-perk">
          <span class="ob-perk-icon">📊</span>
          <div>
            <div class="ob-perk-title">Progress Reports</div>
            <div class="ob-perk-desc">PDF exports, topic accuracy charts and streak tracking for every child</div>
          </div>
        </div>
        <div class="ob-welcome-perk">
          <span class="ob-perk-icon">🔄</span>
          <div>
            <div class="ob-perk-title">Spaced Revision</div>
            <div class="ob-perk-desc">Weak areas resurface automatically at the right intervals for every child</div>
          </div>
        </div>
      </div>

      <div class="ob-footer" style="justify-content: flex-end; margin-top: 20px;">
        <button type="button" class="ob-btn-gold" @click="router.push('/parent/dashboard')">Go to Dashboard →</button>
      </div>
    </div>

    <!-- ── STEP 1: Child setup ── -->
    <div v-else-if="step === 1" class="ob-card">
      <p class="ob-eyebrow">Step 1</p>
      <h2 class="ob-h2">Set up your child's profile</h2>
      <p class="ob-sub">Personalise their experience — everything can be changed later.</p>

      <!-- Name -->
      <div class="ob-field">
        <label class="ob-label">Child's name or nickname</label>
        <input
          ref="nameInput"
          v-model="childName"
          type="text"
          required
          placeholder="e.g. Emma"
          class="ob-input"
        />
        <p class="ob-hint">A nickname is fine — this appears on their learning screens</p>
      </div>

      <!-- Avatar -->
      <div class="ob-field">
        <div class="ob-label">Pick an avatar</div>
        <MTAvatarPicker :selected="selectedAvatar" @select="selectedAvatar = $event" />
      </div>

      <!-- Theme -->
      <div class="ob-field">
        <div class="ob-label">Learning theme</div>
        <p class="ob-hint" style="margin-bottom:12px">Changes how {{ childName || 'their' }}'s progress map looks</p>
        <div class="ob-theme-grid">

          <!-- Space Mission -->
          <button
            type="button"
            class="ob-theme-btn"
            :class="{ 'ob-theme-selected': selectedTheme === 'space-mission' }"
            @click="selectedTheme = 'space-mission'"
          >
            <div class="ob-theme-preview" style="background:#0f172a">
              <svg style="position:absolute;inset:0;width:100%;height:100%">
                <circle cx="8%"  cy="12%" r="1.2" fill="white" opacity="0.8"/>
                <circle cx="22%" cy="8%"  r="0.8" fill="white" opacity="0.6"/>
                <circle cx="40%" cy="18%" r="1.0" fill="white" opacity="0.75"/>
                <circle cx="55%" cy="6%"  r="1.2" fill="white" opacity="0.65"/>
                <circle cx="70%" cy="14%" r="0.8" fill="white" opacity="0.85"/>
                <circle cx="85%" cy="9%"  r="1.0" fill="white" opacity="0.7"/>
                <circle cx="30%" cy="32%" r="0.9" fill="white" opacity="0.6"/>
                <circle cx="60%" cy="28%" r="1.1" fill="white" opacity="0.7"/>
                <circle cx="90%" cy="35%" r="0.8" fill="white" opacity="0.8"/>
                <ellipse cx="25%" cy="55%" rx="18" ry="10" fill="#6366f1" opacity="0.15"/>
                <ellipse cx="72%" cy="62%" rx="14" ry="8" fill="#8b5cf6" opacity="0.12"/>
              </svg>
            </div>
            <div class="ob-theme-label">
              <span class="ob-theme-name">🚀 Space Mission</span>
              <span class="ob-theme-desc">Dark, stars &amp; planets</span>
            </div>
            <div v-if="selectedTheme === 'space-mission'" class="ob-theme-check">✓</div>
          </button>

          <!-- Mountain Trail -->
          <button
            type="button"
            class="ob-theme-btn"
            :class="{ 'ob-theme-selected': selectedTheme === 'mountain-trail' }"
            @click="selectedTheme = 'mountain-trail'"
          >
            <div class="ob-theme-preview" style="background:#faf7f2">
              <svg style="position:absolute;inset:0;width:100%;height:100%" viewBox="0 0 200 56" preserveAspectRatio="none">
                <polygon points="0,56 20,34 45,44 75,24 100,38 130,20 160,36 185,26 200,56" fill="#a8a29e" opacity="0.2"/>
                <polygon points="0,56 15,42 40,50 70,32 100,44 135,28 165,42 185,33 200,56" fill="#44403c" opacity="0.5"/>
                <rect y="50" width="200" height="6" fill="#44403c" opacity="0.65"/>
              </svg>
            </div>
            <div class="ob-theme-label">
              <span class="ob-theme-name">🏔️ Mountain Trail</span>
              <span class="ob-theme-desc">Warm, earthy &amp; bright</span>
            </div>
            <div v-if="selectedTheme === 'mountain-trail'" class="ob-theme-check">✓</div>
          </button>

          <!-- Road Map -->
          <button
            type="button"
            class="ob-theme-btn"
            :class="{ 'ob-theme-selected': selectedTheme === 'road-map' }"
            @click="selectedTheme = 'road-map'"
          >
            <div class="ob-theme-preview" style="background:#f0fdfa">
              <svg style="position:absolute;inset:0;width:100%;height:100%" viewBox="0 0 200 56" preserveAspectRatio="none">
                <ellipse cx="20" cy="40" rx="18" ry="7" fill="#bbf7d0" opacity="0.65"/>
                <ellipse cx="160" cy="38" rx="16" ry="6" fill="#86efac" opacity="0.55"/>
                <path d="M 80,56 Q 100,38 90,20 Q 82,8 110,2" stroke="#99f6e4" stroke-width="12" fill="none" opacity="0.7"/>
                <path d="M 80,56 Q 100,38 90,20 Q 82,8 110,2" stroke="#2dd4bf" stroke-width="2" fill="none" stroke-dasharray="5,5" opacity="0.5"/>
                <rect width="200" height="7" fill="#115e59" opacity="0.8"/>
              </svg>
            </div>
            <div class="ob-theme-label">
              <span class="ob-theme-name">🗺️ Road Map</span>
              <span class="ob-theme-desc">Clean, teal &amp; modern</span>
            </div>
            <div v-if="selectedTheme === 'road-map'" class="ob-theme-check">✓</div>
          </button>

          <!-- Standard -->
          <button
            type="button"
            class="ob-theme-btn"
            :class="{ 'ob-theme-selected': selectedTheme === 'standard' }"
            @click="selectedTheme = 'standard'"
          >
            <div class="ob-theme-preview" style="background:#ffffff">
              <svg style="position:absolute;inset:0;width:100%;height:100%" viewBox="0 0 200 56" preserveAspectRatio="none">
                <rect width="200" height="56" fill="#ffffff"/>
                <rect width="200" height="12" fill="#1e3a8a"/>
                <rect x="12" y="20" width="80" height="4" rx="2" fill="#e2e8f0"/>
                <rect x="12" y="28" width="60" height="4" rx="2" fill="#e2e8f0"/>
                <rect x="12" y="36" width="70" height="4" rx="2" fill="#e2e8f0"/>
                <rect x="120" y="18" width="68" height="30" rx="4" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
                <rect x="128" y="25" width="40" height="4" rx="2" fill="#dbeafe"/>
                <rect x="128" y="33" width="32" height="4" rx="2" fill="#dbeafe" opacity="0.7"/>
              </svg>
            </div>
            <div class="ob-theme-label">
              <span class="ob-theme-name">📐 Standard</span>
              <span class="ob-theme-desc">Clean, blue &amp; classic</span>
            </div>
            <div v-if="selectedTheme === 'standard'" class="ob-theme-check">✓</div>
          </button>

        </div>
      </div>

      <!-- Error -->
      <div v-if="stepError" class="ob-error">
        {{ stepError }}
        <RouterLink v-if="stepLimitError" to="/parent/subscription" class="ob-error-link">View plans →</RouterLink>
      </div>

      <div class="ob-footer" style="justify-content: flex-end;">
        <button type="button" :disabled="loading" class="ob-btn-gold" @click="submitStep1">
          {{ loading ? 'Saving…' : 'Next →' }}
        </button>
      </div>
    </div>

    <!-- ── STEP 2: Skills Check intro ── -->
    <div v-else-if="step === 2" class="ob-card">
      <div class="ob-step3-header">
        <span class="ob-step3-icon">🎯</span>
        <p class="ob-eyebrow" style="margin-bottom:6px">{{ isUpgrade ? 'Step 1' : 'Step 2' }}</p>
        <h2 class="ob-h2" style="margin-bottom:8px">One last thing, {{ childName || 'almost there' }}!</h2>
        <p class="ob-sub">A short Skills Check lets {{ childName || 'them' }} begin at exactly the right level — not too easy, not too hard.</p>
      </div>

      <!-- Info cards -->
      <div class="ob-info-cards">

        <div class="ob-info-card ob-ic-1">
          <div class="ob-ic-icon">⏱️</div>
          <div class="ob-ic-body">
            <div class="ob-fc-title-row">
              <p class="ob-ic-title">About 15–20 minutes</p>
              <span class="ob-fc-tag">Quick</span>
            </div>
            <p class="ob-ic-text">Covers all topic areas at a comfortable pace</p>
          </div>
        </div>

        <div class="ob-info-card ob-ic-2">
          <div class="ob-ic-icon">🎲</div>
          <div class="ob-ic-body">
            <div class="ob-fc-title-row">
              <p class="ob-ic-title">Nothing to pass or fail</p>
              <span class="ob-fc-tag">No pressure</span>
            </div>
            <p class="ob-ic-text">It's diagnostic — the only goal is finding the right starting point</p>
          </div>
        </div>

        <div class="ob-info-card ob-ic-3">
          <div class="ob-ic-icon">🚀</div>
          <div class="ob-ic-body">
            <div class="ob-fc-title-row">
              <p class="ob-ic-title">Personalised from day one</p>
              <span class="ob-fc-tag">Auto</span>
            </div>
            <p class="ob-ic-text">Results shape {{ childName || 'their' }}'s first week of lessons automatically</p>
          </div>
        </div>

      </div>

      <!-- Sample question preview -->
      <div class="ob-preview">
        <div class="ob-preview-label">Preview — sample question</div>
        <div class="ob-preview-q">What is ¾ as a percentage?</div>
        <div class="ob-preview-opt">A. 25%</div>
        <div class="ob-preview-opt ob-preview-opt-correct">B. 75% ✓</div>
        <div class="ob-preview-opt">C. 34%</div>
        <div class="ob-preview-opt">D. 43%</div>
      </div>

      <!-- Actions -->
      <div class="ob-step3-actions">
        <button type="button" :disabled="loading" class="ob-btn-gold ob-btn-full" @click="startSkillsCheck">
          {{ loading ? 'Getting ready…' : `Start ${childName || 'their'}'s Skills Check` }}
        </button>
        <button type="button" class="ob-btn-outline ob-btn-full" @click="skipSkillsCheck">
          Skip for now — start from the beginning
        </button>
      </div>

      <div class="ob-footer ob-footer-nav" style="margin-top:8px">
        <button v-if="!isUpgrade" type="button" class="ob-btn-back" @click="goBack">← Back</button>
        <span v-else></span>
        <span class="ob-footer-note">Skills Check available any time from the dashboard</span>
      </div>
    </div>

    <!-- ── STEP 3: Inline Skills Check ── -->
    <div v-else-if="step === 3">
      <InlineSkillsCheck
        :childId="childId"
        :childName="childName"
        :autoStart="true"
        @back="goBack"
        @complete="onSkillsCheckComplete"
        @stopped="onSkillsCheckStopped"
      />
    </div>

  </div>
</template>

<script setup>
import { ref, inject, computed, watch, nextTick, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import MTAvatarPicker from '@/components/ui/MTAvatarPicker.vue';
import InlineSkillsCheck from '@/components/onboarding/InlineSkillsCheck.vue';
import { useAuth } from '@/composables/useAuth';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { useStudentStore } from '@/stores/studentStore';
import { useUserStore } from '@/stores/userStore';
import * as authService from '@/services/authService';
import { accountApi } from '@/services/accountApi';
import { useAnalytics } from '@/composables/useAnalytics';

const router = useRouter();
const route = useRoute();
const { session } = useAuth();
const preferencesStore = usePreferencesStore();
const { track } = useAnalytics();
const studentStore = useStudentStore();
const userStore = useUserStore();

const currentStep = inject('onboardingStep', ref(1));

// Upgrade mode: parent already onboarded on free plan, now upgrading to paid.
// Accessed via /onboarding?upgrade=true from SubscriptionPage after upgrade.
const isUpgrade = computed(() => route.query.upgrade === 'true');

// Pro→Family upgrade: skills check already done, just show welcome + go to dashboard.
const isSkipSkills = computed(() => route.query.skipSkills === 'true');

// Determine if the current plan is paid (anything other than free).
const isPaidPlan = computed(() => {
  const plan = userStore.session?.effectivePlan || userStore.session?.plan || 'free';
  return plan !== 'free';
});

const step = ref(1);
const nameInput = ref(null);
const childName = ref('');
const selectedAvatar = ref('🦁');
const selectedTheme = ref('standard');
const stepError = ref('');
const stepLimitError = ref(false);
const loading = ref(false);
const childId = ref(null);

onMounted(() => {
  track('onboarding_started', {
    is_upgrade: isUpgrade.value,
    skip_skills: isSkipSkills.value,
    plan: userStore.session?.effectivePlan || userStore.session?.plan || 'free',
  });

  if (isUpgrade.value) {
    const firstChild = studentStore.students[0];
    if (firstChild) {
      childId.value = firstChild.id;
      childName.value = firstChild.name;
    }
    if (isSkipSkills.value) {
      // Pro→Family upgrade: show welcome screen only (step 1), no skills check.
      step.value = 1;
      currentStep.value = 1;
    } else {
      // Free→Pro upgrade: skip child setup, go directly to Skills Check intro (step 2).
      step.value = 2;
      currentStep.value = 1;
    }
  } else {
    // Focus the name input on initial load for child setup.
    nextTick(() => nameInput.value?.focus());
  }
});

// Re-focus the name input whenever the user navigates back to step 1.
watch(step, val => {
  if (val === 1) nextTick(() => nameInput.value?.focus());
});

function goBack() {
  if (step.value === 2 && !isUpgrade.value) {
    step.value = 1;
    currentStep.value = 1;
  } else if (step.value === 3) {
    step.value = 2;
  }
}

async function submitStep1() {
  stepError.value = '';
  stepLimitError.value = false;
  if (!childName.value.trim()) {
    stepError.value = 'Please enter your child\'s name.';
    return;
  }
  loading.value = true;
  try {
    const uid = session.value?.uid;
    if (!uid) throw new Error('Not authenticated');

    let newChildId;
    try {
      // Call the API first — a 403 means the plan's child limit has been reached.
      const result = await accountApi.addChild(uid, childName.value.trim());
      newChildId = result.child.childId;
      // Sync to localStorage with the same ID so offline fallback stays consistent.
      authService.addChild(uid, childName.value.trim(), newChildId);
      track('child_added', { child_name: childName.value.trim() });
    } catch (apiError) {
      if (apiError.status === 403) {
        // Child limit exceeded — show a clear message and do not proceed.
        stepLimitError.value = true;
        stepError.value = apiError.data?.message
          || 'You\'ve reached the child limit for your current plan. Please upgrade to add more children.';
        return;
      }
      // Non-403 (network / server error) — fall back to localStorage only so
      // local development and offline use still work.
      console.warn('[onboarding] API unavailable, using localStorage:', apiError);
      newChildId = authService.addChild(uid, childName.value.trim());
    }

    childId.value = newChildId;
    preferencesStore.setAvatar(newChildId, selectedAvatar.value);
    preferencesStore.setHomeTheme(selectedTheme.value, newChildId);

    // Free plan: onboarding ends after child setup — skip Skills Check.
    if (!isPaidPlan.value) {
      await _completeOnboarding();
      router.push('/parent/dashboard');
      return;
    }

    // Paid plan: continue to Skills Check.
    step.value = 2;
    currentStep.value = 2;
  } catch (e) {
    stepError.value = 'Something went wrong. Please try again.';
    console.log(e)
  } finally {
    loading.value = false;
  }
}

async function _completeOnboarding() {
  const uid = session.value?.uid;
  if (uid) {
    authService.markOnboardingComplete(uid);
    await accountApi.updateAccount(uid, { onboardingComplete: true })
      .catch(e => console.warn('[onboarding] Failed to sync onboardingComplete:', e));
    await studentStore.loadChildren(uid);
  }
  track('onboarding_completed', {
    is_upgrade: isUpgrade.value,
    plan: userStore.session?.effectivePlan || userStore.session?.plan || 'free',
  });
}

function startSkillsCheck() {
  step.value = 3;
  track('onboarding_skills_check_started');
}

async function onSkillsCheckComplete() {
  await _completeOnboarding();
  router.push(`/student/${childId.value}/dashboard`);
}

async function onSkillsCheckStopped() {
  track('onboarding_skills_check_abandoned');
  await _completeOnboarding();
  router.push('/parent/dashboard');
}

async function skipSkillsCheck() {
  track('onboarding_skills_check_skipped');
  await _completeOnboarding();
  router.push('/parent/dashboard');
}
</script>

<style scoped>
/* ── Card wrapper ── */
.ob-card {
  background: #F9F6EF;
  border-radius: 8px;
  padding: 40px;
  box-shadow: 0 40px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(184,144,42,0.15);
  font-family: 'Jost', sans-serif;
}

/* ── Typography ── */
.ob-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #B8902A;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.ob-eyebrow::before {
  content: '';
  width: 22px;
  height: 1px;
  background: #B8902A;
  flex-shrink: 0;
}
.ob-h2 {
  font-family: 'Cormorant Garamond', serif;
  font-size: 30px;
  font-weight: 600;
  color: #0D1628;
  line-height: 1.2;
  margin-bottom: 6px;
  letter-spacing: -0.01em;
}
.ob-sub {
  font-size: 15px;
  color: #555;
  font-weight: 400;
  margin-bottom: 28px;
  line-height: 1.6;
}

/* ── Fields ── */
.ob-field {
  margin-bottom: 24px;
}
.ob-label {
  font-size: 11px;
  font-weight: 700;
  color: #555;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 8px;
  display: block;
}
.ob-input {
  width: 100%;
  border: 1.5px solid #e0dbd0;
  border-radius: 4px;
  padding: 11px 14px;
  font-size: 14px;
  font-family: 'Jost', sans-serif;
  font-weight: 400;
  color: #0D1628;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;
}
.ob-input:focus { border-color: #B8902A; }
.ob-input::placeholder { color: #ccc; }
.ob-hint {
  font-size: 12px;
  color: #aaa;
  margin-top: 6px;
  font-weight: 300;
}

/* ── Theme picker ── */
.ob-theme-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.ob-theme-btn {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  border: 1.5px solid #e0dbd0;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}
.ob-theme-btn:hover { border-color: rgba(184,144,42,0.5); }
.ob-theme-selected {
  border-color: #B8902A !important;
  box-shadow: 0 0 0 2px rgba(184,144,42,0.15);
}
.ob-theme-preview {
  position: relative;
  height: 56px;
  width: 100%;
  overflow: hidden;
}
.ob-theme-label {
  padding: 8px 10px;
}
.ob-theme-name {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #0D1628;
}
.ob-theme-desc {
  display: block;
  font-size: 10px;
  color: #999;
  margin-top: 1px;
}
.ob-theme-check {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #B8902A;
  color: #0D1628;
  font-size: 9px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Error ── */
.ob-error {
  margin-bottom: 18px;
  background: #fdf2f2;
  border: 1px solid #f5c6c6;
  color: #b91c1c;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 13px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}
.ob-error-link {
  color: #b91c1c;
  font-weight: 700;
  text-decoration: underline;
  white-space: nowrap;
  font-size: 13px;
}

/* ── Buttons ── */
.ob-btn-gold {
  background: #B8902A;
  color: #0D1628;
  border: none;
  border-radius: 4px;
  padding: 12px 28px;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Jost', sans-serif;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.ob-btn-gold:hover:not(:disabled) {
  background: #d4a843;
  transform: translateY(-1px);
}
.ob-btn-gold:disabled { opacity: 0.55; cursor: not-allowed; }
.ob-btn-full { width: 100%; padding: 14px; font-size: 13px; }

.ob-btn-outline {
  background: transparent;
  color: #B8902A;
  border: 1.5px solid rgba(184,144,42,0.4);
  border-radius: 4px;
  padding: 12px 28px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Jost', sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
}
.ob-btn-outline:hover {
  border-color: #B8902A;
  background: rgba(184,144,42,0.04);
}

.ob-btn-back {
  background: transparent;
  border: none;
  color: #B8902A;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Jost', sans-serif;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0;
}
.ob-btn-back:hover { color: #d4a843; }

/* ── Footer ── */
.ob-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 20px;
  border-top: 1px solid #e8e4da;
  margin-top: 8px;
}
.ob-footer-nav { margin-top: 20px; }
.ob-footer-note {
  font-size: 13px;
  color: #888;
  font-weight: 400;
}

/* ── Step 2 info cards ── */
.ob-fc-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 5px;
  flex-wrap: wrap;
}
.ob-fc-tag {
  font-size: 10px;
  font-weight: 700;
  background: rgba(184,144,42,0.12);
  color: #7a5c10;
  padding: 3px 10px;
  border-radius: 100px;
  white-space: nowrap;
  letter-spacing: 0.04em;
}

/* ── Step 2 (Skills Check) ── */
.ob-step3-header {
  text-align: center;
  margin-bottom: 24px;
}
.ob-step3-icon { font-size: 48px; display: block; margin-bottom: 14px; }
.ob-info-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}
.ob-info-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border-radius: 6px;
  padding: 14px 18px;
  border: 1px solid transparent;
}
.ob-ic-1 { background: rgba(184,144,42,0.06); border-color: rgba(184,144,42,0.15); }
.ob-ic-2 { background: rgba(34,197,94,0.05); border-color: rgba(34,197,94,0.15); }
.ob-ic-3 { background: rgba(59,130,246,0.05); border-color: rgba(59,130,246,0.1); }
.ob-ic-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.ob-ic-body { flex: 1; min-width: 0; }
.ob-ic-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 20px;
  font-weight: 600;
  color: #0D1628;
  margin: 0 0 3px;
}
.ob-ic-text {
  font-size: 14px;
  color: #444;
  font-weight: 400;
  line-height: 1.55;
  margin: 0;
}

/* Sample question preview */
.ob-preview {
  border: 1px solid #e0dbd0;
  border-radius: 6px;
  padding: 18px;
  background: #fff;
  margin-bottom: 20px;
}
.ob-preview-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #B8902A;
  margin-bottom: 12px;
}
.ob-preview-q {
  font-family: 'Cormorant Garamond', serif;
  font-size: 20px;
  font-weight: 600;
  color: #0D1628;
  margin-bottom: 12px;
  line-height: 1.4;
}
.ob-preview-opt {
  border: 1.5px solid #e0dbd0;
  border-radius: 4px;
  padding: 9px 13px;
  font-size: 14px;
  color: #333;
  margin-top: 6px;
  font-family: 'Jost', sans-serif;
  font-weight: 500;
}
.ob-preview-opt-correct {
  border-color: #B8902A;
  background: rgba(184,144,42,0.07);
  color: #7a5c10;
  font-weight: 600;
}

.ob-step3-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 8px;
}

/* ── Welcome card (pro→family upgrade) ── */
.ob-welcome-card { text-align: center; }
.ob-welcome-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #B8902A, #d4a843);
  color: #0D1628;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 4px 16px;
  border-radius: 100px;
  margin-bottom: 20px;
}
.ob-welcome-icon {
  font-size: 52px;
  margin-bottom: 16px;
  display: block;
}
.ob-welcome-h2 { font-size: 28px; margin-bottom: 10px; }
.ob-welcome-sub { max-width: 400px; margin: 0 auto 28px; }
.ob-welcome-perks {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
  margin-bottom: 8px;
}
.ob-welcome-perk {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  background: rgba(184,144,42,0.06);
  border: 1px solid rgba(184,144,42,0.15);
  border-radius: 6px;
}
.ob-perk-icon { font-size: 22px; flex-shrink: 0; margin-top: 2px; }
.ob-perk-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 16px;
  font-weight: 600;
  color: #0D1628;
  margin-bottom: 2px;
}
.ob-perk-desc { font-size: 12px; color: #666; font-weight: 400; line-height: 1.5; }
.ob-perk-done {
  font-size: 10px;
  font-weight: 700;
  color: #16a34a;
  background: rgba(22,163,74,0.1);
  padding: 2px 8px;
  border-radius: 100px;
  margin-left: 6px;
  white-space: nowrap;
  vertical-align: middle;
}

@media (max-width: 480px) {
  .ob-card { padding: 28px 20px; }
  .ob-theme-grid { grid-template-columns: 1fr; }
}
</style>
